import {
  Button,
  PHONE_COUNTRIES,
  PhoneInput,
  type Country,
} from "@/components";
import { ControlledInput } from "@/components/input/controlledInput";
import { PublicRoutes } from "@/constants/routes";
import { useAuth } from "@/context";
import { openWhatsApp } from "@/pages/landingPage/utils/whatsapp";
import { useForgotPasswordPrefillStore } from "@/store/forgotPasswordPrefillStore";
import { useOtpLoginPrefillStore } from "@/store/otpLoginPrefillStore";
import { phoneUtils } from "@/utils";
import { LOGIN_REDIRECT_PARAM, rememberLoginRedirect } from "@/utils/loginRedirect";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router";
import { rememberCookieOptIn } from "@/utils/webClient";
import { authScheme, type AuthScheme } from "./authScheme";

export const Auth = () => {
  const { login, isLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    rememberCookieOptIn(searchParams);
  }, [searchParams]);
  const redirectParam = searchParams.get(LOGIN_REDIRECT_PARAM);

  // The OTP and password-reset flows go through other /auth URLs: the
  // redirect is kept aside here and used when any of them logs in. Opening
  // the login without one forgets a redirect left by an abandoned visit.
  useEffect(() => {
    rememberLoginRedirect(redirectParam);
  }, [redirectParam]);
  const setForgotPasswordPrefill = useForgotPasswordPrefillStore(
    state => state.setPrefill
  );
  const setOtpLoginPrefill = useOtpLoginPrefillStore(state => state.setPrefill);

  const [country, setCountry] = useState<Country>(PHONE_COUNTRIES[0]);

  const { control, handleSubmit, formState, watch, setValue } =
    useForm<AuthScheme>({
      values: {
        phone: "",
        password: "",
      },
      resolver: zodResolver(authScheme),
    });

  const phoneValue = watch("phone") ?? "";
  // Brasil aceita fixo (10) ou celular (11); demais países usam só `length`.
  const minPhoneLength = country.minLength ?? country.length;
  const isPhoneComplete =
    phoneValue.length >= minPhoneLength && phoneValue.length <= country.length;

  const handleCountryChange = (next: Country) => {
    setCountry(next);
    // Trunca o número quando o novo país tiver menos dígitos.
    setValue("phone", phoneValue.slice(0, next.length), {
      shouldValidate: true,
    });
  };

  const onSubmit = (data: AuthScheme) => {
    const national = phoneUtils.cleanPhoneNumber(data.phone);
    // Brasil mantém o formato atual (sem DDI); demais países enviam DDI + número.
    const phone =
      country.code === "BR" ? national : `${country.ddi}${national}`;
    login({ phone, password: data.password });
  };

  const handleSSOLogin = () => {
    // TODO: redirecionar para o provedor de SSO (SAML/OIDC) quando o backend estiver disponível.
  };

  const handleForgotPassword = () => {
    if (isPhoneComplete) {
      setForgotPasswordPrefill(phoneValue, country);
    }
    navigate(PublicRoutes.AUTH_FORGOT_PASSWORD);
  };

  const handleOtpLogin = () => {
    if (isPhoneComplete) {
      setOtpLoginPrefill(phoneValue, country);
    }
    navigate(PublicRoutes.AUTH_OTP);
  };

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold text-black">Acessar conta</h1>
        <p className="text-base text-gray-500">
          Entre com seu celular e senha para continuar.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
        <Controller
          control={control}
          name="phone"
          render={({ field }) => (
            <PhoneInput
              label="Celular"
              autoFocus
              country={country}
              onCountryChange={handleCountryChange}
              value={field.value ?? ""}
              onChange={field.onChange}
            />
          )}
        />

        <div className="flex flex-col gap-2">
          <ControlledInput
            control={control}
            name="password"
            type="password"
            label="Senha"
            placeholder="Digite sua senha"
          />
          <button
            type="button"
            onClick={handleForgotPassword}
            className="cursor-pointer self-end text-sm text-primary hover:underline"
          >
            Esqueci minha senha
          </button>
        </div>

        <Button
          isLoading={isLoading || formState.isSubmitting}
          type="submit"
          disabled={!formState.isValid || !isPhoneComplete}
        >
          Entrar
        </Button>
      </form>

      <div className="flex items-center gap-4">
        <div className="h-px flex-1 bg-gray-200" />
        <span className="text-sm text-gray-500">ou</span>
        <div className="h-px flex-1 bg-gray-200" />
      </div>

      <div className="flex flex-col gap-3">
        <button
          type="button"
          onClick={handleOtpLogin}
          className="flex h-11 w-full cursor-pointer items-center justify-center rounded-md border border-gray-200 text-base text-black transition-colors hover:bg-gray-50"
        >
          Entrar com código via WhatsApp
        </button>
      </div>

      <p className="text-center text-sm text-gray-500">
        Ainda não tem uma conta?{" "}
        <button
          type="button"
          onClick={() => openWhatsApp("auth")}
          className="cursor-pointer font-medium text-primary hover:underline"
        >
          Conhecer a Fidexa
        </button>
      </p>
    </div>
  );
};
